import { UpgradeSubscriptionModal } from '@components'

import { ButtonDropdown, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { getBlockCreateDepositInvoiceActionModalId } from '@features/invoices/elements/BlockCreateInvoiceActionModal/utils/getBlockCreateDepositInvoiceActionModalId'
import { getBlockCreateInvoiceActionModalId } from '@features/invoices/elements/BlockCreateInvoiceActionModal/utils/getBlockCreateInvoiceActionModalId'
import { BlockCreateInvoiceActionModals } from '@features/invoices/elements/BlockCreateInvoiceActionModals'
import { DepositInvoiceDetailsModal } from '@features/invoices/elements/DepositInvoiceDetailsModal'
import { getDepositInvoiceDetailsModalProps } from '@features/invoices/elements/DepositInvoiceDetailsModal/utils/getDepositInvoiceDetailsModalProps'
import { DepositInvoiceUnavailableModal } from '@features/invoices/elements/DepositInvoiceUnavailableModal'
import { MarkQuoteAsAcceptedConfirmationModal } from '@features/invoices/elements/MarkQuoteAsAcceptedConfirmationModal'
import { QuoteActionType } from '@features/invoices/enums/quoteActionType'
import { QuoteStatus } from '@features/invoices/enums/quoteStatus'
import { useConvertQuoteToDepositInvoice } from '@features/invoices/hooks/quotes/useConvertQuoteToDepositInvoice'
import { useConvertQuoteToInvoice } from '@features/invoices/hooks/quotes/useConvertQuoteToInvoice'
import { useGuardedAction } from '@features/invoices/hooks/useGuardedAction'
import { Quote } from '@features/invoices/types/quotes/quote'
import { getBlockCreateInvoiceActionReason } from '@features/invoices/utils/depositInvoices/getBlockCreateInvoiceActionReason'
import { getQuoteDepositInvoice } from '@features/invoices/utils/depositInvoices/getQuoteDepositInvoice'
import { getQuoteRegularInvoiceDraft } from '@features/invoices/utils/depositInvoices/getQuoteRegularInvoiceDraft'
import { getDiscountModeTrackingValue } from '@features/invoices/utils/getDiscountModeTrackingValue'
import { getQuoteDiscountMode } from '@features/invoices/utils/getQuoteDiscountMode'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { Scope } from '../../../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useComponentId } from '../../../../../../../../../../hooks'
import { isAuthorized } from '../../../../../../../../../../utils/isAuthorized'

interface ConvertQuoteDropdownProps {
  quoteStatus?: QuoteStatus
  quote?: Quote
  regularUrl?: string
}

export const ConvertQuoteDropdown = ({ quoteStatus, regularUrl, quote }: ConvertQuoteDropdownProps): ReactElement => {
  const { organization } = useUserOrganization()
  const { t } = useTranslation()
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)

  const depositInvoiceUpgradeModalId = `${ModalId.UpgradeSubscriptionModal}-deposit-invoice`
  const depositInvoiceUnavailableModalId = ModalId.DepositInvoiceUnavailableModal

  const { open: openUpgradeModal } = useModal(upgradeModalId)
  const { open: openDepositInvoiceUpgradeModal } = useModal(depositInvoiceUpgradeModalId)
  const { open: openDepositInvoiceUnavailableModal } = useModal(depositInvoiceUnavailableModalId)
  const { open: openBlockCreateInvoiceActionModal } = useModal(getBlockCreateInvoiceActionModalId())
  const { open: openBlockCreateDepositInvoiceActionModal } = useModal(getBlockCreateDepositInvoiceActionModalId())

  const convertQuoteToDepositInvoiceConfirmationModalId = ModalId.ConvertQuoteToDepositInvoiceConfirmationModal
  const depositInvoiceDetailsModalId = ModalId.DepositInvoiceDetailsModal

  const blockCreateInvoiceActionReason = getBlockCreateInvoiceActionReason(quote)
  const existingDepositInvoice = getQuoteDepositInvoice(quote)
  const existingRegularInvoiceDraft = getQuoteRegularInvoiceDraft(quote)
  const depositModalProps = getDepositInvoiceDetailsModalProps(quote)
  const trackingDiscountMode = getDiscountModeTrackingValue(getQuoteDiscountMode(quote))

  const isCurrencyDifferent = quote?.summary?.foreignCurrency !== organization?.baseCurrencyId

  const { onConvertClick: onConvertToInvoiceClick } = useConvertQuoteToInvoice({
    convertToInvoiceUrl: regularUrl,
    existingInvoiceDraft: existingRegularInvoiceDraft,
    onUpgradeClick: isAuthorized(Scope.OfferConvertToInvoiceUpsell) ? openUpgradeModal : undefined,
    trackingDiscountMode,
    trackingEventName: 'XXX - Invoicing Core - Quote View - Convert to invoice button clicked',
    trackingOption: 'invoice',
  })

  const handleConvertToInvoiceClick = useGuardedAction({
    isBlocked: !!blockCreateInvoiceActionReason,
    openBlockingModal: openBlockCreateInvoiceActionModal,
    proceed: onConvertToInvoiceClick,
  })

  const {
    onConfirmConvert: onConfirmConvertToDepositInvoice,
    onConvertClick: onConvertToDepositInvoiceClick,
    onDepositInvoiceDetailsClose,
    onDepositInvoiceDetailsConfirm,
  } = useConvertQuoteToDepositInvoice({
    confirmationModalId: convertQuoteToDepositInvoiceConfirmationModalId,
    depositInvoiceDetailsModalId,
    existingDepositInvoice,
    onUpgradeClick: isAuthorized(Scope.OfferConvertToDepositInvoiceUpsell) ? openDepositInvoiceUpgradeModal : undefined,
    quoteId: quote?.id,
    quoteStatus,
    trackingDiscountMode,
    trackingEventName: 'XXX - Invoicing Core - Quote View - Convert to invoice button clicked',
  })

  const handleConvertToDepositInvoiceClick = useGuardedAction({
    isBlocked: !!existingRegularInvoiceDraft,
    openBlockingModal: openBlockCreateDepositInvoiceActionModal,
    proceed: onConvertToDepositInvoiceClick,
  })

  const quoteActionsMenuItems: NavItem<QuoteActionType>[] = [
    {
      children: t('quotes.view.actions.convert_dropdown.deposit_invoice'),
      iconLeft: 'files',
      id: QuoteActionType.ConvertToDepositInvoice,
      value: QuoteActionType.ConvertToDepositInvoice,
    },
    {
      children: t('quotes.view.actions.convert_dropdown.invoice'),
      iconLeft: 'fileText',
      id: QuoteActionType.ConvertToInvoice,
      value: QuoteActionType.ConvertToInvoice,
    },
  ]

  const handleSelect = useCallback(
    (_id: string, value: QuoteActionType) => {
      switch (value) {
        case QuoteActionType.ConvertToDepositInvoice:
          if (isCurrencyDifferent) {
            openDepositInvoiceUnavailableModal()
          } else {
            handleConvertToDepositInvoiceClick()
          }
          break
        case QuoteActionType.ConvertToInvoice:
          handleConvertToInvoiceClick()
          break
        default:
          break
      }
    },
    [
      handleConvertToDepositInvoiceClick,
      handleConvertToInvoiceClick,
      isCurrencyDifferent,
      openDepositInvoiceUnavailableModal,
    ],
  )

  return (
    <>
      <ButtonDropdown items={quoteActionsMenuItems} onSelect={handleSelect} variant="filled">
        {t('quotes.view.actions.convert_dropdown.create')}
      </ButtonDropdown>
      <MarkQuoteAsAcceptedConfirmationModal
        id={convertQuoteToDepositInvoiceConfirmationModalId}
        onConfirm={onConfirmConvertToDepositInvoice}
        trackingOption="depositInvoice"
      />
      <DepositInvoiceDetailsModal
        {...depositModalProps}
        id={depositInvoiceDetailsModalId}
        onClose={onDepositInvoiceDetailsClose}
        onConfirm={onDepositInvoiceDetailsConfirm}
      />

      <UpgradeSubscriptionModal
        id={depositInvoiceUpgradeModalId}
        trackingContext={TrackingContext.DepositInvoiceUpsellQuoteDetails}
      />

      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.QuoteConvertToInvoice} />

      <BlockCreateInvoiceActionModals
        createInvoiceUrl={regularUrl}
        onCreateDepositInvoiceClick={onConvertToDepositInvoiceClick}
        quote={quote}
      />

      <DepositInvoiceUnavailableModal
        id={depositInvoiceUnavailableModalId}
        onCreateRegularInvoice={handleConvertToInvoiceClick}
      />
    </>
  )
}
